/**
 * <s-app-banner> is the "Get the Ashva app" install card shown on every page.
 * It appears a few seconds after the page loads for visitors who have not
 * installed the store or dismissed the card in the last 14 days.
 * - [data-install]: uses the browser's install prompt when available (Android
 *   Chrome, desktop Chrome/Edge); otherwise its commandfor/command opens the
 *   step-by-step help dialog (iPhone and other browsers).
 * - [data-dismiss]: hides the card and remembers it for 14 days.
 */
const STORAGE_KEY = 'ashva-app-banner-dismissed';
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

class SAppBanner extends HTMLElement {
  connectedCallback() {
    this.installButton = this.querySelector('[data-install]');
    this.deferredPrompt = null;

    this.onBeforeInstall = (event) => {
      event.preventDefault();
      this.deferredPrompt = event;
    };
    this.onInstalled = () => this.hide(true);
    this.onClick = async (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('[data-dismiss]')) {
        this.hide(true);
        return;
      }
      if (target.closest('[data-install]') && this.deferredPrompt) {
        event.preventDefault();
        event.stopPropagation();
        const prompt = this.deferredPrompt;
        this.deferredPrompt = null;
        prompt.prompt();
        const choice = await prompt.userChoice.catch(() => null);
        if (choice && choice.outcome === 'accepted') this.hide(true);
      }
    };

    window.addEventListener('beforeinstallprompt', this.onBeforeInstall);
    window.addEventListener('appinstalled', this.onInstalled);
    this.addEventListener('click', this.onClick, true);

    if (this.isInstalled() || this.isSnoozed()) return;
    const delay = Number(this.dataset.delay) || 8000;
    this.timer = window.setTimeout(() => {
      this.hidden = false;
      requestAnimationFrame(() => this.setAttribute('data-open', ''));
    }, delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    window.removeEventListener('beforeinstallprompt', this.onBeforeInstall);
    window.removeEventListener('appinstalled', this.onInstalled);
    this.removeEventListener('click', this.onClick, true);
  }

  isInstalled() {
    return window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
  }

  isSnoozed() {
    try {
      const at = Number(localStorage.getItem(STORAGE_KEY));
      return at > 0 && Date.now() - at < SNOOZE_MS;
    } catch (error) {
      return false;
    }
  }

  hide(remember) {
    window.clearTimeout(this.timer);
    this.removeAttribute('data-open');
    window.setTimeout(() => {
      this.hidden = true;
    }, 300);
    if (remember) {
      try {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
      } catch (error) {
        /* Storage blocked: the card simply shows again next visit. */
      }
    }
  }
}

if (!customElements.get('s-app-banner')) customElements.define('s-app-banner', SAppBanner);
